import { lazy, type ComponentType } from "react";

// Guards against a reload loop: set right before we force a reload, cleared on
// the next successful chunk load. Lives in sessionStorage so it survives the
// reload but not a new tab.
export const RELOAD_KEY = "chunk-reload-v1";

type Factory<T extends ComponentType<unknown>> = () => Promise<{ default: T }>;

/**
 * Runs a dynamic-import factory, recovering from the Vite "stale chunk"
 * failure that happens when a tab predates a deploy: the content-hashed chunk
 * it references (e.g. `Produse-BIW6WIWK.js`) is gone from Hosting, so the
 * `import()` rejects. On the first such failure we force one full reload so the
 * browser re-fetches the current `index.html` + chunks. A second failure means
 * the reload didn't help (genuinely broken deploy or dead network), so we let
 * the error surface instead of looping.
 */
export async function loadWithReload<T extends ComponentType<unknown>>(
  factory: Factory<T>,
): Promise<{ default: T }> {
  try {
    const mod = await factory();
    sessionStorage.removeItem(RELOAD_KEY); // success → re-arm the guard
    return mod;
  } catch (err) {
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
      // The reload takes over the page; never resolve so nothing renders first.
      return new Promise<{ default: T }>(() => {});
    }
    throw err; // reload already attempted → genuine failure, surface it
  }
}

/** Drop-in replacement for `React.lazy` with stale-chunk reload recovery. */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: Factory<T>,
) {
  return lazy(() => loadWithReload(factory));
}
